import { useState, useMemo } from 'react';
import { Eye, Check, X, TrendingUp, Clock, CheckCircle2, AlertCircle, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { YourCheckaNavigation } from '@/components/Navbar';
import EarningDetailsModal from '@/components/modals/EarningDetailsModal';
import WithdrawalDetailsModal from '@/components/modals/WithdrawalDetailsModal';
import { 
  useGetAllWithdrawalsQuery,
  useGetWithdrawalStatisticsQuery,
  useReviewWithdrawalMutation,
  useProcessWithdrawalMutation,
  useGetAllEarningsQuery,
  useMarkEarningAsPaidMutation,
  useBulkMarkEarningsAsPaidMutation,
} from '@/store/api/apiSlice';
import { Withdrawal, WithdrawalStatus, Earning, EarningStatus } from '@/types';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

type FilterType = 'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed';
type ViewType = 'withdrawals' | 'earnings';

export default function AdminWithdrawals() {
  const currentUser = useAppSelector(selectCurrentUser);
  const [view, setView] = useState<ViewType>('earnings');
  const [filter, setFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [earningsFilter, setEarningsFilter] = useState<'pending' | 'paid'>('pending');
  const [selectedEarning, setSelectedEarning] = useState<Earning | null>(null);
  const [showEarningModal, setShowEarningModal] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);

  // Fetch data
  const { data: withdrawals = [], isLoading: isLoadingWithdrawals, error: withdrawalsError } = useGetAllWithdrawalsQuery({});
  const { data: statistics } = useGetWithdrawalStatisticsQuery();
  const { data: earningsData, isLoading: isLoadingEarnings, error: earningsError } = useGetAllEarningsQuery({ status: earningsFilter === 'pending' ? EarningStatus.PENDING : EarningStatus.PAID });
  const [reviewWithdrawal, { isLoading: isReviewing }] = useReviewWithdrawalMutation();
  const [processWithdrawal, { isLoading: isProcessing }] = useProcessWithdrawalMutation();
  const [markEarningAsPaid] = useMarkEarningAsPaidMutation();
  const [bulkMarkEarningsAsPaid] = useBulkMarkEarningsAsPaidMutation();

  // Safely extract earnings array from response
  const earnings = Array.isArray(earningsData) ? earningsData : [];

  const isLoading = view === 'withdrawals' ? isLoadingWithdrawals : isLoadingEarnings;

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Filter withdrawals
  const filteredData = useMemo(() => {
    return withdrawals.filter((w) => {
      if (filter === 'all') return true;
      return w.status.toLowerCase() === filter;
    });
  }, [withdrawals, filter]);

  // Pagination for withdrawals
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  // Pagination for earnings
  const paginatedEarnings = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return earnings.slice(startIndex, startIndex + rowsPerPage);
  }, [earnings, currentPage, rowsPerPage]);

  const totalPages = view === 'withdrawals' 
    ? Math.ceil(filteredData.length / rowsPerPage)
    : Math.ceil(earnings.length / rowsPerPage);

  const handleRowSelect = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const handleApprove = async (id: string) => {
    try {
      await reviewWithdrawal({ 
        id, 
        data: { 
          status: 'approved',
          reviewNotes: 'Approved by admin'
        } 
      }).unwrap();
    } catch (error) {
      console.error('Failed to approve withdrawal:', error);
    }
  };

  const handleReject = async (id: string) => {
    try {
      await reviewWithdrawal({ 
        id, 
        data: { 
          status: 'rejected',
          reviewNotes: 'Rejected by admin'
        } 
      }).unwrap();
    } catch (error) {
      console.error('Failed to reject withdrawal:', error);
    }
  };

  const handleMarkCompleted = async (id: string) => {
    try {
      await processWithdrawal({ 
        id, 
        data: { 
          status: 'completed',
          transactionReference: `TXN-${Date.now()}`
        } 
      }).unwrap();
    } catch (error) {
      console.error('Failed to mark as completed:', error);
    }
  };

  const handleMarkEarningPaid = async (id: string) => {
    try {
      await markEarningAsPaid(id).unwrap();
      setShowEarningModal(false);
      setSelectedEarning(null);
    } catch (error) {
      console.error('Failed to mark earning as paid:', error);
    }
  };

  const handleBulkMarkPaid = async () => {
    try {
      const earningIds = Array.from(selectedRows).map(index => {
        const earning = paginatedEarnings[index];
        return earning?.id;
      }).filter(Boolean) as string[];
      
      if (earningIds.length === 0) return;
      
      await bulkMarkEarningsAsPaid({ earningIds }).unwrap();
      setSelectedRows(new Set());
    } catch (error) {
      console.error('Failed to bulk mark as paid:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status: WithdrawalStatus) => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case WithdrawalStatus.APPROVED:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case WithdrawalStatus.PROCESSING:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case WithdrawalStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-100';
      case WithdrawalStatus.REJECTED:
        return 'bg-red-50 text-red-700 border-red-100';
      case WithdrawalStatus.FAILED:
        return 'bg-gray-50 text-gray-700 border-gray-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const columns: Column<Withdrawal>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (_item, index) => (
        <span className="text-sm text-gray-700">
          {(currentPage - 1) * rowsPerPage + (index || 0) + 1}
        </span>
      ),
    },
    {
      key: 'agent',
      label: 'Agent',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">
            {item.user?.firstName} {item.user?.lastName}
          </p>
          <p className="text-xs text-gray-500">{item.user?.email}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm font-semibold text-gray-900">
          {formatCurrency(item.amount)}
        </span>
      ),
    },
    {
      key: 'bankAccount',
      label: 'Bank Account',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">{item.bankAccount?.bankName}</p>
          <p className="text-xs text-gray-500">{item.bankAccount?.accountNumber}</p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${getStatusColor(item.status)} px-3 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Requested',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">
          {new Date(item.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="flex items-center gap-2">
          {item.status === WithdrawalStatus.PENDING && (
            <>
              <Button
                size="sm"
                onClick={() => handleApprove(item.id)}
                disabled={isReviewing}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 text-xs"
              >
                <Check className="w-3 h-3 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                onClick={() => handleReject(item.id)}
                disabled={isReviewing}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-xs"
              >
                <X className="w-3 h-3 mr-1" />
                Reject
              </Button>
            </>
          )}
          {item.status === WithdrawalStatus.APPROVED && (
            <Button
              size="sm"
              onClick={() => handleMarkCompleted(item.id)}
              disabled={isProcessing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-xs"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Mark Paid
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="px-3 py-1 text-xs"
            title="View Details"
            onClick={() => {
              setSelectedWithdrawal(item);
              setShowWithdrawalModal(true);
            }}
          >
            <Eye className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ];

  const earningsColumns: Column<Earning>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (_item, index) => (
        <span className="text-sm text-gray-700">
          {(currentPage - 1) * rowsPerPage + (index || 0) + 1}
        </span>
      ),
    },
    {
      key: 'agent',
      label: 'Agent',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">
            {item.agent?.firstName} {item.agent?.lastName}
          </p>
          <p className="text-xs text-gray-500">{item.agent?.email}</p>
        </div>
      ),
    },
    {
      key: 'investigation',
      label: 'Investigation',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div>
          <p className="text-sm text-gray-900 font-medium">
            {item.employeeName || 
              `${item.investigation?.employeeFirstName || ''} ${item.investigation?.employeeLastName || ''}`.trim() || 
              'N/A'}
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {item.checkType || item.investigation?.checkType || 'N/A'}
          </p>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm font-semibold text-gray-900">
          {formatCurrency(
            typeof item.amountEarned === 'string' 
              ? parseFloat(item.amountEarned) 
              : item.amountEarned || item.amount
          )}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${
            item.status === EarningStatus.PAID
              ? 'bg-green-50 text-green-700 border-green-100'
              : 'bg-yellow-50 text-yellow-700 border-yellow-100'
          } px-3 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date Earned',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">
          {new Date(item.createdAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <Button
          size="sm"
          onClick={() => {
            setSelectedEarning(item);
            setShowEarningModal(true);
          }}
          className="bg-gray-900 hover:bg-gray-800 text-white px-3 py-1 text-xs"
        >
          <Eye className="w-3 h-3 mr-1" />
          View
        </Button>
      ),
    },
  ];

  // Error handling for earnings
  if (earningsError && view === 'earnings') {
    const errorMessage = (earningsError as any)?.data?.message || (earningsError as any)?.message || 'Failed to load earnings data';
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Earnings</h3>
            <p className="text-red-600 mb-4">{errorMessage}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              Retry
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Error handling for withdrawals
  if (withdrawalsError && view === 'withdrawals') {
    const errorMessage = (withdrawalsError as any)?.data?.message || (withdrawalsError as any)?.message || 'Failed to load withdrawals data';
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Withdrawals</h3>
            <p className="text-red-600 mb-4">{errorMessage}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              Retry
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (isLoading) {
    return (
      <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
        <div className="bg-white rounded-3xl shadow-sm flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <YourCheckaNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
          userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 md:p-8 lg:p-10">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Earnings & Withdrawals</h1>
                <p className="text-gray-600 mt-1">Manage agent earnings and withdrawal requests</p>
              </div>
            </div>

            {/* View Tabs */}
            <div className="flex items-center gap-2 mb-4 md:mb-6 border-b border-gray-200 overflow-x-auto">
              <button
                onClick={() => {
                  setView('earnings');
                  setCurrentPage(1);
                  setSelectedRows(new Set());
                }}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  view === 'earnings'
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <DollarSign className="w-4 h-4 inline mr-2" />
                Earnings Approval
              </button>
              <button
                onClick={() => {
                  setView('withdrawals');
                  setCurrentPage(1);
                  setSelectedRows(new Set());
                }}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  view === 'withdrawals'
                    ? 'border-[#FF6B35] text-[#FF6B35]'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <TrendingUp className="w-4 h-4 inline mr-2" />
                Withdrawal Management
              </button>
            </div>

            {/* Summary Cards - Conditional */}
            {view === 'withdrawals' ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-yellow-50 rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <p className="text-sm text-yellow-600 font-medium">Pending Review</p>
                  </div>
                  <p className="text-3xl font-bold text-yellow-900">
                    {formatCurrency(statistics?.totalAmountPending || 0)}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    {statistics?.pendingRequests || 0} requests
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-sm text-blue-600 font-medium">Approved</p>
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-blue-900">
                    {formatCurrency((statistics?.totalAmountRequested || 0) - (statistics?.totalAmountCompleted || 0) - (statistics?.totalAmountPending || 0))}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {statistics?.approvedRequests || 0} requests
                  </p>
                </div>

                <div className="bg-green-50 rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-sm text-green-600 font-medium">Completed</p>
                  </div>
                  <p className="text-3xl font-bold text-green-900">
                    {formatCurrency(statistics?.totalAmountCompleted || 0)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {statistics?.completedRequests || 0} requests
                  </p>
                </div>

                <div className="bg-red-50 rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <p className="text-sm text-red-600 font-medium">Rejected</p>
                  </div>
                  <p className="text-3xl font-bold text-red-900">
                    {'\u20A6'}0
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {statistics?.rejectedRequests || 0} requests
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-gradient-to-r from-[#FF6B35]/10 to-orange-50 rounded-xl p-4 md:p-6 border border-orange-100">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Earnings Approval</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {earnings.filter(e => e.status === EarningStatus.PENDING).length}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Total: {formatCurrency(earnings.filter(e => e.status === EarningStatus.PENDING).reduce((sum, e) => sum + e.amount, 0))}
                  </p>
                </div>
                {selectedRows.size > 0 && earningsFilter === 'pending' && (
                  <Button
                    onClick={handleBulkMarkPaid}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Mark {selectedRows.size} as Paid
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Filters - Conditional */}
          {view === 'withdrawals' ? (
            <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 overflow-x-auto">
              <button
                onClick={() => setFilter('all')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'pending'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('approved')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'approved'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'completed'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Completed
              </button>
              <button
                onClick={() => setFilter('rejected')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === 'rejected'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Rejected
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6 overflow-x-auto">
              <button
                onClick={() => {
                  setEarningsFilter('pending');
                  setCurrentPage(1);
                }}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  earningsFilter === 'pending'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Pending Approval
              </button>
              <button
                onClick={() => {
                  setEarningsFilter('paid');
                  setCurrentPage(1);
                }}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  earningsFilter === 'paid'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Paid
              </button>
            </div>
          )}

          {/* Table - Conditional */}
          <div className="border border-gray-100 rounded-2xl overflow-hidden mb-6">
            {view === 'withdrawals' ? (
              paginatedData.length > 0 ? (
                <DataTable
                  data={paginatedData}
                  columns={columns}
                  showCheckbox={true}
                  selectedRows={selectedRows}
                  onRowSelect={handleRowSelect}
                  hoverable={true}
                  striped={true}
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <p className="text-gray-500">No withdrawal requests found</p>
                </div>
              )
            ) : (
              paginatedEarnings.length > 0 ? (
                <DataTable
                  data={paginatedEarnings}
                  columns={earningsColumns}
                  showCheckbox={earningsFilter === 'pending'}
                  selectedRows={selectedRows}
                  onRowSelect={handleRowSelect}
                  hoverable={true}
                  striped={true}
                />
              ) : (
                <div className="flex items-center justify-center h-64">
                  <p className="text-gray-500">
                    {earningsFilter === 'pending' ? 'No pending earnings found' : 'No paid earnings found'}
                  </p>
                </div>
              )
            )}
          </div>

          {/* Pagination */}
          {((view === 'withdrawals' && paginatedData.length > 0) || (view === 'earnings' && paginatedEarnings.length > 0)) && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Rows per page:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ‹
                </button>

                {currentPage > 2 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      1
                    </button>
                    {currentPage > 3 && <span className="px-2 text-gray-400">...</span>}
                  </>
                )}

                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    {currentPage - 1}
                  </button>
                )}

                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#FF6B35] text-white">
                  {currentPage}
                </button>

                {currentPage < totalPages && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                  >
                    {currentPage + 1}
                  </button>
                )}

                {currentPage < totalPages - 1 && (
                  <>
                    {currentPage < totalPages - 2 && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Earning Details Modal */}
      <EarningDetailsModal
        isOpen={showEarningModal}
        onClose={() => {
          setShowEarningModal(false);
          setSelectedEarning(null);
        }}
        earning={selectedEarning}
        onMarkPaid={handleMarkEarningPaid}
        isLoading={false}
      />

      {/* Withdrawal Details Modal */}
      <WithdrawalDetailsModal
        isOpen={showWithdrawalModal}
        onClose={() => {
          setShowWithdrawalModal(false);
          setSelectedWithdrawal(null);
        }}
        withdrawal={selectedWithdrawal}
        onApprove={handleApprove}
        onReject={handleReject}
        onMarkPaid={handleMarkCompleted}
        isLoading={isReviewing || isProcessing}
      />
    </main>
  );
}
